import { useLocalStorageState } from 'front-end/src/app-admin/useLocalStorageState'
import {
  Box,
  ButtonClean,
  ButtonOutline,
  Link,
  Tooltip
} from 'front-end/src/components'
import IconExclamation from 'front-end/src/components/common/icon/IconExclamation'
import IconInfo from 'front-end/src/components/common/icon/IconInfo'
import { OrganisationCalendars } from 'front-end/src/graphql/public/PublicSchemaTypes'
import { useGetCalendarIntegrationsForOrganisationQuery } from 'front-end/src/pages/account/authentication-tab/AuthenticationTab.codegen'
import {
  CalendarIntegrationEnum,
  defaultIntegrationOption
} from 'front-end/src/pages/account/authentication-tab/calendar-integrations/CalendarIntegrations'
import { useLooopTheme } from 'front-end/src/public-api/hooks/useLooopTheme'
import React, { FC } from 'react'
import { staticLinks } from 'shared/linkBuilder'
import { BoxProps, Flex } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'

import {
  usePushUnSyncedEventsMutation,
  useUnSyncedFutureEventsQuery
} from './SyncAlert.codegen'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'

const dismissLocalStorageKey = 'dismissSyncAlert'

export const SyncAlert: FC<
  BoxProps & {
    hasDismiss: boolean
  }
> = (props) => {
  const { isAdmin } = usePublicApiSession()
  const { theme } = useLooopTheme()
  const { hasDismiss, ...rest } = props
  const [alreadyDismissed, setAlreadyDismissed] = useLocalStorageState(
    dismissLocalStorageKey,
    false
  )
  const [pushUnSyncedEvents, { loading }] = usePushUnSyncedEventsMutation()
  const { data, refetch } = useUnSyncedFutureEventsQuery()
  const countOfUnsyncedEvent =
    data?.admin?.organisation?.unSyncedEvents.length || 0
  const { data: calendarsData } =
    useGetCalendarIntegrationsForOrganisationQuery()

  const calendars = calendarsData?.admin?.organisation?.calendars
  const integration = defaultIntegrationOption(
    calendars as OrganisationCalendars
  )

  return isAdmin &&
    countOfUnsyncedEvent &&
    (!alreadyDismissed || !hasDismiss) ? (
    <Flex
      bgColor={theme.colors.yellow['50']}
      borderRadius={theme.radii.base}
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      flexGrow={1}
      padding="12px"
      {...rest}
    >
      <IconExclamation color={theme.colors.grey[700]} mr="15px" />
      <Box flexGrow={1}>
        <Trans>
          It looks like you have <strong>{countOfUnsyncedEvent} events</strong>{' '}
          in the future which are not synced with{' '}
          {integration === CalendarIntegrationEnum.GOOGLE
            ? 'Google Calendar'
            : 'MS calendar'}
        </Trans>
      </Box>

      {hasDismiss && (
        <>
          <Tooltip
            text={
              <Trans>
                You can always find this in{' '}
                <Link to={staticLinks.accountAuthenticationOptions}>
                  account authentication options
                </Link>
              </Trans>
            }
          >
            <IconInfo color={theme.colors.grey[500]} />
          </Tooltip>
          <ButtonClean
            color={theme.colors.grey[700]}
            _hover={{
              color: `${theme.colors.grey[900]}`
            }}
            mr="8px"
            ml="6px"
            onClick={() => {
              setAlreadyDismissed(true)
            }}
          >
            <Trans>Dismiss</Trans>
          </ButtonClean>
        </>
      )}
      <ButtonOutline
        isLoading={loading}
        loadingText={t`Processing...`}
        onClick={async () => {
          await pushUnSyncedEvents()
          await refetch()
        }}
      >
        <Trans>Push missing future events</Trans>
      </ButtonOutline>
    </Flex>
  ) : null
}
